import heroImage from "@/assets/images/admin-hero-bg.jpg";
import Header from "@/components/ui/Header";
import { ROUTES } from "@/constants/routes";
import { Card, Input, Button, Form, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import type { LoginRequest } from "@/types/Auth/login";
import { Auth } from "@/services/auth.service";
import { toast } from "sonner";
import { useState } from "react";
import { useAuthStore } from "@/store/auth.store";
import { wait } from "@/utils/wait";

const { Title } = Typography;

const REQUIRED_COLOR = "#C00";

function renderLabel(text: string, required = false) {
  return (
    <span style={{ fontWeight: 600 }} className="text-sm!">
      {text}
      {required ? <span style={{ color: REQUIRED_COLOR }}>*</span> : null}
    </span>
  );
}

function requiredRule(message: string) {
  return [{ required: true, message }];
}

export default function AuthPage() {
  usePageTitle();
  const navigate = useNavigate();
   const [isSubmitting, setIsSubmitting] = useState(false);

  const useMockLogin = import.meta.env.VITE_USE_MOCK_LOGIN === "true";

  const initialValues = useMockLogin
    ? {
        email: import.meta.env.VITE_MOCK_LOGIN_EMAIL,
        password: import.meta.env.VITE_MOCK_LOGIN_PASSWORD,
      }
    : undefined;

  const handleLogin = async (values: LoginRequest) => {
   if (isSubmitting) return;

    const startTime = Date.now();

    try {
      setIsSubmitting(true);

      const res = await Auth(values);

      if (!res.status || !res.data) {
        toast.error(res.message || "Login failed");
        return;
      }

      useAuthStore.getState().login(res.data);

      const elapsed = Date.now() - startTime;
      const minimumLoadingDuration = 500;

      if (elapsed < minimumLoadingDuration) {
        await wait(minimumLoadingDuration - elapsed);
      }

      await wait(500);
      message.success("Log masuk berjaya");

      navigate(ROUTES.adminDashboard, {
        replace: true,
      });
    } catch (error) {
      console.error("Log masuk gagal:", error);

      message.error( error instanceof Error ? error.message : "Log masuk gagal", );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative w-full min-h-screen flex flex-col items-center justify-center px-4 py-10 sm:py-14">
      <img
        src={heroImage}
        alt="Admin Login"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="relative z-10 flex flex-col mx-auto max-w-6xl w-full items-center gap-4 ">
        <Header title="" />

        <Title
          level={2}
          className="text-2xl font-bold! text-primary! text-center md:text-4xl mt-4"
        >
          Log Masuk ke Sistem Maklumat
        </Title>

        <Card className="w-full max-w-xl shadow-xl!">
          <Form
            layout="vertical"
            requiredMark={false}
            onFinish={handleLogin}
            initialValues={initialValues}
            scrollToFirstError
          >
            <Form.Item
              label={renderLabel("Nama pengguna", true)}
              name="email"
              rules={requiredRule("Sila masukkan nama pengguna")}
            >
              <Input placeholder="Nama pengguna" size="large" />
            </Form.Item>
            <Form.Item
              label={renderLabel("Kata laluan", true)}
              name="password"
              rules={requiredRule("Sila masukkan kata laluan")}
            >
              <Input.Password placeholder="Kata laluan anda" size="large" />
            </Form.Item>

            <Button
              type="primary"
              size="large"
              htmlType="submit"
              className="w-full"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Log masuk
            </Button>
          </Form>
        </Card>
      </div>
    </section>
  );
}
